import { useEffect, useRef, useState, useCallback } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { api } from "../lib/api";

/**
 * Production-grade Agora live streaming component.
 * Best practices applied:
 *  - mode: 'live' + role-based client (host/audience)
 *  - VP8 codec (broadest browser support)
 *  - Custom 1280x720@30fps 1500-2500kbps video (optimizationMode: detail) for host
 *  - music_high_quality_stereo audio profile (48kHz stereo ~192kbps) + AEC/ANS/AGC
 *  - Dual-stream mode: host publishes high+low streams
 *  - Stream fallback: audience auto-switches to low under poor network
 *  - Backend-signed token auth (1-hour TTL)
 *  - Connection state listener → graceful UI reflect of reconnects
 *  - Live network-quality indicator
 *  - Audience auto-subscribes; idempotent on re-renders
 *  - Hard cleanup of tracks + client on unmount
 */
export default function LiveVideo({ channel, mode = "audience", autoJoin = true, uid: forcedUid, onHostPublished }) {
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const clientRef = useRef(null);
  const tracksRef = useRef([]);
  const camTrackRef = useRef(null);
  const micTrackRef = useRef(null);
  const joinedRef = useRef(false);  // guards double-join in StrictMode
  const [joined, setJoined] = useState(false);
  const [connState, setConnState] = useState("DISCONNECTED");
  const [quality, setQuality] = useState({ uplink: 0, downlink: 0 });
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [err, setErr] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCamId, setActiveCamId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [micMuted, setMicMuted] = useState(false);

  const toggleMic = useCallback(async () => {
    if (mode !== "host" || !micTrackRef.current) return;
    const nextMuted = !micMuted;
    try {
      // Use setEnabled (not setMuted) — setEnabled fully stops publishing
      // audio data to the SDK, which is the only reliable way to guarantee
      // the audience actually hears silence. setMuted has edge cases where
      // the track keeps sending faint background audio in some browsers.
      await micTrackRef.current.setEnabled(!nextMuted);
      setMicMuted(nextMuted);
    } catch (e) {
      console.error("[Agora] mic toggle failed", e);
    }
  }, [mode, micMuted]);

  const switchCamera = useCallback(async () => {
    if (mode !== "host" || !camTrackRef.current || cameras.length < 2 || switching) return;
    const idx = cameras.findIndex((c) => c.deviceId === activeCamId);
    const next = cameras[(idx + 1) % cameras.length];
    if (!next) return;
    setSwitching(true);
    try {
      await camTrackRef.current.setDevice(next.deviceId);
      setActiveCamId(next.deviceId);
    } catch (e) {
      console.error("[Agora] switch camera failed", e);
    } finally {
      setSwitching(false);
    }
  }, [cameras, activeCamId, mode, switching]);

  const join = useCallback(async () => {
    if (joinedRef.current) return;
    joinedRef.current = true;
    try {
      setErr(null);
      const uid = forcedUid ?? Math.floor(Math.random() * 1_000_000);
      // 1. Get fresh signed token from our backend
      const r = await api.get(`/agora/token`, { params: { channel, uid, role: mode } });
      const { appId, token } = r.data;

      // 2. Build a live-mode client with VP8 (broadest support)
      const client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
      clientRef.current = client;
      await client.setClientRole(mode === "host" ? "host" : "audience");

      // 3. Connection state → UI
      client.on("connection-state-change", (cur, prev, reason) => {
        setConnState(cur);
        if (reason) console.info("[Agora] state change reason:", reason);
      });

      // 4. Live network quality indicator (uplink + downlink, 1=excellent → 6=down)
      client.on("network-quality", (stats) => {
        setQuality({
          uplink: stats.uplinkNetworkQuality,
          downlink: stats.downlinkNetworkQuality,
        });
      });

      // 5. Remote user lifecycle
      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "video" && remoteRef.current) user.videoTrack?.play(remoteRef.current);
        if (mediaType === "audio") user.audioTrack?.play();
        setRemoteUsers((u) => [...u.filter((x) => x.uid !== user.uid), user]);
      });
      client.on("user-unpublished", (user) => setRemoteUsers((u) => u.filter((x) => x.uid !== user.uid)));
      client.on("user-left", (user) => setRemoteUsers((u) => u.filter((x) => x.uid !== user.uid)));

      // Token will expire in 30s → silently fetch a fresh one and renew
      client.on("token-privilege-will-expire", async () => {
        try {
          const r = await api.get(`/agora/token`, { params: { channel, uid, role: mode } });
          await client.renewToken(r.data.token);
          console.info("[Agora] token renewed");
        } catch (e) {
          console.error("[Agora] token renew failed", e);
        }
      });

      // 6. Join the channel
      await client.join(appId, channel, token, uid);

      // 7. Audience-only: enable stream fallback (0=disabled, 1=low-quality on
      //    weak network, 2=audio-only fallback). Use option 1 so viewers on
      //    decent networks always watch the high 720p stream; only downshift
      //    to the low stream when uplink is genuinely poor.
      if (mode !== "host") {
        try { await client.setStreamFallbackOption(uid, 1); } catch (e) { console.info("[Agora] fallback opt skipped", e?.message); }
      }

      // 8. Host: create tracks with high-fidelity audio + high-bitrate 720p
      //    video. music_high_quality_stereo = 48kHz stereo ≈192kbps (much
      //    clearer voice + product-sound reproduction than music_standard).
      //    Video is a custom 720p profile pushing 2500kbps and framerate 30
      //    with prioritise-detail mode for better sharpness on product close-ups.
      //    AEC/ANS/AGC are enabled explicitly so hosts on laptops/phones don't
      //    ship echo or background hiss to the audience.
      if (mode === "host") {
        const [micTrack, camTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
          {
            encoderConfig: "music_high_quality_stereo",
            AEC: true,   // acoustic echo cancellation
            ANS: true,   // automatic noise suppression
            AGC: true,   // automatic gain control (evens loud/quiet moments)
          },
          {
            encoderConfig: {
              width: 1280,
              height: 720,
              frameRate: 30,
              bitrateMin: 1500,
              bitrateMax: 2500,
            },
            optimizationMode: "detail", // prioritises visual clarity for product shots
          }
        );
        tracksRef.current = [micTrack, camTrack];
        micTrackRef.current = micTrack;
        camTrackRef.current = camTrack;
        camTrack.play(localRef.current);
        await client.publish([micTrack, camTrack]);
        // Notify parent that host is now publishing — used to gate the Start-Drop button
        if (typeof onHostPublished === "function") {
          try { onHostPublished(true); } catch (e) { console.warn("onHostPublished cb error:", e); }
        }
        // Dual-stream so audience on weak networks can subscribe to a 320x180 low stream
        try { await client.enableDualStream(); } catch (e) { console.info("[Agora] dual-stream not available:", e?.message); }
        // Enumerate cameras for the switch-camera toggle
        try {
          const list = await AgoraRTC.getCameras();
          setCameras(list);
          // Surface the actually-active deviceId
          const ms = camTrack.getMediaStreamTrack?.();
          const cur = ms?.getSettings?.().deviceId;
          setActiveCamId(cur || list[0]?.deviceId || null);
        } catch (e) { console.info("[Agora] camera enumeration failed", e?.message); }
      }
      setJoined(true);
    } catch (e) {
      joinedRef.current = false;
      console.error("[Agora] join error", e);
      setErr(humanizeError(e));
    }
  }, [channel, mode, forcedUid]);

  const leave = useCallback(async () => {
    try {
      tracksRef.current.forEach((t) => { try { t.stop(); t.close(); } catch (err) { console.warn("[Agora] track teardown failed:", err?.message); } });
      tracksRef.current = [];
      camTrackRef.current = null;
      micTrackRef.current = null;
      if (clientRef.current) {
        clientRef.current.removeAllListeners();
        await clientRef.current.leave();
        clientRef.current = null;
      }
      joinedRef.current = false;
      setJoined(false);
      setRemoteUsers([]);
      setConnState("DISCONNECTED");
      setCameras([]);
      setActiveCamId(null);
      setMicMuted(false);
    } catch (e) {
      console.error("[Agora] leave error", e);
    }
  }, []);

  useEffect(() => {
    if (autoJoin && channel) join();
    return () => { leave(); };
  }, [channel, autoJoin, mode, join, leave]);

  return (
    <div className="relative w-full h-full bg-[#F8F7F2] border border-[#E5E1D8] overflow-hidden" data-testid="live-video">
      {mode === "host" && <div ref={localRef} className="absolute inset-0" data-testid="local-video" />}
      {mode === "audience" && <div ref={remoteRef} className="absolute inset-0" data-testid="remote-video" />}

      {/* Connecting overlay */}
      {!joined && !err && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#FFFFFF]/85">
          <div className="text-center font-mono-ds">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E] mb-2">Connecting Feed</div>
            <div className="text-2xl text-[#161616]">Joining channel…</div>
          </div>
        </div>
      )}
      {err && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#FFFFFF]/90">
          <div className="text-center font-mono-ds max-w-md px-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6A00] mb-2">Stream Error</div>
            <div className="text-lg text-[#161616]">{err}</div>
            <button
              onClick={() => { setErr(null); join(); }}
              className="mt-4 border border-[#FF6A00] text-[#FF6A00] hover:bg-[#FF6A00] hover:text-[#161616] font-mono-ds uppercase text-xs tracking-widest px-4 py-2 transition-colors"
              data-testid="retry-stream-btn"
            >
              ↻ Retry
            </button>
          </div>
        </div>
      )}

      {/* Audience: waiting-for-host placeholder */}
      {joined && mode === "audience" && remoteUsers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#FFFFFF]/90">
          <div className="text-center font-mono-ds">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E] mb-2">Awaiting Host</div>
            <div className="text-xl text-[#161616]">Host has not started the feed yet</div>
          </div>
        </div>
      )}

      {/* LIVE badge */}
      <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#FFFFFF]/85 px-3 py-1 border border-[#E5E1D8]">
        <span className="w-2 h-2 bg-[#FF6A00] pulse-dot" />
        <span className="font-mono-ds text-xs uppercase tracking-widest text-[#161616]">LIVE</span>
      </div>

      {/* Host-only controls toolbar (only shown when host is published) */}
      {mode === "host" && joined && (
        <div className="absolute bottom-4 left-4 flex items-center gap-2" data-testid="host-controls">
          {/* Mic mute toggle */}
          <button
            type="button"
            onClick={toggleMic}
            aria-pressed={micMuted}
            aria-label={micMuted ? "Unmute microphone" : "Mute microphone"}
            title={micMuted ? "Mic muted — tap to unmute" : "Mic live — tap to mute"}
            className={`flex items-center gap-2 backdrop-blur border px-3 py-2 font-mono-ds text-[10px] uppercase tracking-widest transition-colors ${
              micMuted
                ? "bg-[#FF6A00] border-[#FF6A00] text-[#161616] font-bold shadow-[0_4px_12px_rgba(17,17,17,0.06)]"
                : "bg-[#FFFFFF]/90 border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] text-[#161616]"
            }`}
            data-testid="mic-toggle-btn"
          >
            {micMuted ? (
              // Mic-off (slash) icon
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            ) : (
              // Mic-on icon
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            )}
            <span>{micMuted ? "Muted" : "Mic"}</span>
          </button>

          {/* Camera switcher (only with 2+ cameras) */}
          {cameras.length >= 2 && (
            <button
              type="button"
              onClick={switchCamera}
              disabled={switching}
              className="flex items-center gap-2 bg-[#FFFFFF]/90 backdrop-blur border border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] text-[#161616] px-3 py-2 font-mono-ds text-[10px] uppercase tracking-widest transition-colors disabled:opacity-60"
              data-testid="switch-camera-btn"
              aria-label="Switch camera"
              title={cameras.find((c) => c.deviceId === activeCamId)?.label || "Active camera"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 4l-2 2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3l-2-2H9z" />
                <path d="M14.5 13.5l2.5 2.5-2.5 2.5" />
                <path d="M9.5 13.5L7 16l2.5 2.5" />
                <path d="M7 16h10" />
              </svg>
              <span>{switching ? "Switching…" : `Cam ${(cameras.findIndex((c) => c.deviceId === activeCamId) + 1) || 1}/${cameras.length}`}</span>
            </button>
          )}
        </div>
      )}

      {/* Network quality indicator */}
      {joined && (
        <div
          className="absolute top-4 right-4 bg-[#FFFFFF]/85 border border-[#E5E1D8] px-2 py-1 flex items-center gap-2"
          data-testid="net-quality"
          title={`Conn: ${connState} · Up: ${qualityLabel(quality.uplink)} · Down: ${qualityLabel(quality.downlink)}`}
        >
          <QualityBars value={mode === "host" ? quality.uplink : quality.downlink} />
          <span className="font-mono-ds text-[9px] uppercase tracking-widest text-[#6E6E6E]">
            {qualityLabel(mode === "host" ? quality.uplink : quality.downlink)}
          </span>
        </div>
      )}
    </div>
  );
}

function qualityLabel(q) {
  // Agora returns 0-6: 0=unknown, 1=excellent, 2=good, 3=poor, 4=bad, 5=very bad, 6=down
  if (!q || q === 0) return "—";
  if (q === 1) return "EXCELLENT";
  if (q === 2) return "GOOD";
  if (q === 3) return "POOR";
  if (q === 4) return "BAD";
  if (q === 5) return "VERY BAD";
  return "DOWN";
}

function QualityBars({ value }) {
  // 4 bars: green at 1-2, amber at 3, red at 4-6
  const bars = [1, 2, 3, 4];
  const filled = !value ? 0 : value <= 1 ? 4 : value <= 2 ? 3 : value <= 3 ? 2 : value <= 4 ? 1 : 0;
  const color = filled >= 3 ? "bg-green-400" : filled === 2 ? "bg-yellow-400" : "bg-[#FF6A00]";
  return (
    <div className="flex items-end gap-0.5 h-3">
      {bars.map((b) => (
        <div
          key={b}
          className={`w-0.5 ${b <= filled ? color : "bg-[#E5E1D8]"}`}
          style={{ height: `${30 + b * 18}%` }}
        />
      ))}
    </div>
  );
}

function humanizeError(e) {
  const msg = (e?.message || String(e)).toLowerCase();
  if (msg.includes("permission") || msg.includes("notallowed")) return "Camera / microphone permission denied. Please grant access in your browser and retry.";
  if (msg.includes("devicenotfound") || msg.includes("not found")) return "No camera/mic device detected. Plug one in and retry.";
  if (msg.includes("token")) return "Streaming session expired. Tap retry to reconnect.";
  if (msg.includes("not_supported") || msg.includes("notsupported")) return "Your browser doesn't support this stream. Try Chrome / Edge / Safari 15+.";
  return e?.message || "Couldn't join the live stream";
}
